<template>
  <div class="burger-menu">
    <input type="checkbox" id="burger" v-model="isOpen" />
    <label for="burger" :class="{ 'open': isOpen }">
      <div class="middle-line"></div>
    </label>

    <!-- Overlay now always present but only visible when menu is open -->
    <div
        class="menu-overlay"
        :class="{ 'visible': isOpen }"
        @click.self="closeMenu"
    ></div>

    <div class="menu-content" :class="{ 'menu-open': isOpen, 'chat-active': chatOpen }" @click.self="closeMenu">
      <div class="menu-layout">
        <nav class="menu-navigation" @click="handleNavClick">
          <div class="menu-section">
            <router-link to="/dashboard" class="menu-heading">DASHBOARD</router-link>
          </div>

          <div class="menu-section">
            <router-link to="/devices" class="menu-heading">DEVICES</router-link>
          </div>

          <div class="menu-section">
            <router-link to="/meteringpoints" class="menu-heading">METERING POINTS</router-link>
          </div>

          <div class="menu-section">
            <router-link to="/reading-devices" class="menu-heading">READING DEVICES</router-link>
          </div>

          <div class="menu-section">
            <router-link to="/driveby" class="menu-heading">DRIVE BY</router-link>
          </div>

          <div class="menu-section">
            <router-link to="/loradashboard" class="menu-heading">LORA</router-link>
          </div>

          <div class="menu-section">
            <router-link to="/dlms" class="menu-heading">DLMS</router-link>
          </div>

          <div class="menu-section">
            <router-link to="/user-settings" class="menu-heading">USER SETTINGS</router-link>
          </div>

          <div class="menu-section">
            <a href="#" @click.prevent="logout" class="menu-heading">LOGOUT</a>
          </div>
        </nav>

        <Transition name="menu-chat">
          <LlmChatWidget
              v-if="isOpen && chatOpen"
              class="menu-inline-chat"
              @close="chatOpen = false"
          />
        </Transition>
      </div>

      <AiChatMenuButton
          v-if="isOpen"
          :class="['menu-chat-trigger', { active: chatOpen }]"
          @open="openChat"
      />
    </div>
  </div>
</template>

<style scoped>
.burger-menu {
  position: relative;
}

#burger {
  display: none;
}

#burger:checked + label:before {
  transform: translateY(10px) rotateZ(45deg);
}

#burger:checked + label:after {
  transform: translateY(-10px) rotateZ(-45deg);
}

#burger:checked + label .middle-line {
  opacity: 0;
}

label[for="burger"] {
  color: #FFFFFF;
  cursor: pointer;
  height: 21px;
  margin: auto;
  user-select: none;
  width: 36px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  z-index: 220;
  transition: color 0.3s ease;
}

label[for="burger"].open {
  color: #FFFFFF;
}

label[for="burger"]:before,
label[for="burger"]:after,
label[for="burger"] .middle-line {
  background: currentColor;
  content: "";
  display: block;
  height: 1px;
  transition: transform 0.4s ease;
  width: 100%;
}

label[for="burger"]:before {
  content: "";
}

label[for="burger"]:after {
  content: "";
}

/* Overlay styling */
.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 200;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.4s ease, backdrop-filter 0.4s ease, -webkit-backdrop-filter 0.4s ease;
}

.menu-overlay.visible {
  opacity: 1;
  pointer-events: auto;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.menu-content {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: transparent;
  padding: 0 70px;
  transform: translateX(-100%);
  transition: transform 0.4s ease;
  z-index: 210;
  overflow-y: auto;

  /* Center the content vertically */
  display: flex;
  align-items: center;
}

.menu-content.menu-open {
  transform: translateX(0);
}

.menu-layout {
  display: grid;
  grid-template-columns: minmax(220px, 370px) minmax(0, 1fr);
  align-items: center;
  gap: clamp(42px, 7vw, 132px);
  width: 100%;
  min-height: 100%;
}

/* Menu Navigation Styles */
.menu-navigation {
  font-family: 'Inter', sans-serif;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: min(370px, 100%);
  transition: opacity 0.28s ease, transform 0.4s ease;
}

.menu-content.chat-active .menu-navigation {
  opacity: 0.78;
  transform: translateX(-4px);
}

.menu-section {
  margin-bottom: 8px;
}

.menu-heading {
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.2em;
  margin-bottom: 24px;
  text-transform: uppercase;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: none;
  display: inline-block;
  transform-origin: left center;
  transition: font-size 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94),
              color 0.35s ease,
              letter-spacing 0.35s ease,
              font-weight 0.35s ease;
}

.menu-heading:hover,
.menu-heading.router-link-active,
.menu-heading.router-link-exact-active {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 1);
}

.menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.menu-item {
  font-size: 11px;
  font-weight: 200;
  letter-spacing: 0.15em;
  color: white;
  text-decoration: none;
  display: inline-block; /* Changed from block to inline-block */
  text-transform: uppercase;
  transition: opacity 0.2s ease;
  padding-left: 20px; /* Added left padding for submenu items */
}

.menu-item:hover {
  opacity: 0.8;
}

.menu-chat-trigger {
  position: fixed;
  right: 70px;
  bottom: 58px;
  z-index: 232;
}

.menu-chat-trigger.active {
  filter: drop-shadow(0 18px 36px rgba(91, 182, 255, 0.22));
}

.menu-inline-chat {
  justify-self: stretch;
  width: min(100%, 980px);
  min-width: 0;
}

.menu-chat-enter-active,
.menu-chat-leave-active {
  transition:
      opacity 0.32s ease,
      transform 0.42s cubic-bezier(0.22, 1, 0.36, 1),
      filter 0.32s ease;
}

.menu-chat-enter-from,
.menu-chat-leave-to {
  opacity: 0;
  filter: blur(8px);
  transform: translateX(28px) scale(0.985);
}

@media (max-width: 680px) {
  .menu-content {
    padding: 0 34px;
  }

  .menu-layout {
    display: flex;
    align-items: center;
  }

  .menu-content.chat-active {
    align-items: stretch;
    padding-top: 86px;
    padding-bottom: 108px;
  }

  .menu-content.chat-active .menu-layout {
    min-height: auto;
  }

  .menu-content.chat-active .menu-navigation {
    display: none;
  }

  .menu-chat-trigger {
    right: 24px;
    bottom: 28px;
  }
}
</style>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import AiChatMenuButton from './AiChatMenuButton.vue';
import LlmChatWidget from './LlmChatWidget.vue';

const router = useRouter();
const isOpen = ref(false);
const chatOpen = ref(false);

const closeMenu = () => {
  chatOpen.value = false;
  isOpen.value = false;
};

const openChat = () => {
  chatOpen.value = true;
};

watch(isOpen, (open) => {
  if (!open) chatOpen.value = false;
});

// Logout function that clears all localStorage and redirects to login
const logout = () => {
  // Clear all localStorage items
  localStorage.clear();

  // Alternative: if you only want to clear specific items
  /*
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_info');
  localStorage.removeItem('user_role');
  localStorage.removeItem('tenant_id');
  localStorage.removeItem('accessible_tenants');
  localStorage.removeItem('permissions');
  localStorage.removeItem('token_expires_at');
  localStorage.removeItem('remember_user');
  */

  // Close the menu
  closeMenu();

  // Redirect to login page
  router.push('/login');
};

// Handle clicks within the navigation area
const handleNavClick = (event) => {
  // If click is not on a link, close the menu
  if (!event.target.closest('a')) {
    closeMenu();
  }
};

// Close menu when clicking anywhere outside the menu content
const handleOutsideClick = (event) => {
  // Check if menu is open and click is outside menu items
  if (isOpen.value && !event.target.closest('.menu-navigation') &&
      !event.target.closest('.menu-chat-trigger') &&
      !event.target.closest('.llm-chat-panel') &&
      !event.target.closest('.llm-artifact-download-link') &&
      !event.target.closest('label[for="burger"]')) {
    closeMenu();
  }
};

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
});
</script>
